import { useGetManyUsers } from "#src/components/hooks/adapters/useUsers";
import { EmptyState, TimelinePanel } from "@validereinc/common-components";
import {
  type UserReturnType,
  type WorkerVersionType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import styles from "./WorkerDetailTimelinePanelDrawer.module.scss";
import { WorkerVersionCard } from "./WorkerVersionCard";

const cx = classNames.bind(styles);

type WorkerDetailTimelinePanelDrawerProps = {
  drawerKey: string;
  isLoading: boolean;
  versions: WorkerVersionType[];
  selectedVersion?: number;
  latestVersionNumber?: number;
  onVersionSelect: (version: number) => void;
};

export const WorkerDetailTimelinePanelDrawer = ({
  drawerKey,
  isLoading,
  versions,
  selectedVersion,
  latestVersionNumber,
  onVersionSelect,
}: WorkerDetailTimelinePanelDrawerProps) => {
  const metadataUsersQueries = useGetManyUsers(
    Array.from(
      new Set(
        versions
          .map((version) => version.created_by)
          .filter((createdBy): createdBy is string => Boolean(createdBy))
      )
    )
  );
  const metadataUsersMap = useMemo(
    () =>
      metadataUsersQueries.reduce<Record<string, UserReturnType>>(
        (map, query) => {
          if (!query.data) {
            return map;
          }

          map[query.data.id] = query.data;
          return map;
        },
        {}
      ),
    [metadataUsersQueries]
  );

  return (
    <TimelinePanel.Drawer
      className={cx("timeline-panel")}
      shouldRenderPortal={false}
      drawerKey={drawerKey}
    >
      <TimelinePanel.Header
        title="Versions"
        className={cx("timeline-panel__header")}
      />
      <TimelinePanel.Content
        isLoading={isLoading}
        className={cx("timeline-panel__content")}
      >
        {versions.length > 0 ? (
          <ol className={cx("timeline-panel__list")}>
            {[...versions]
              .sort((a, b) => b.version - a.version)
              .map((version) => (
                <WorkerVersionCard
                  key={version.id}
                  version={version}
                  isSelected={selectedVersion === version.version}
                  latestVersionNumber={latestVersionNumber}
                  metadataUsersMap={metadataUsersMap}
                  onSelect={onVersionSelect}
                />
              ))}
          </ol>
        ) : (
          <EmptyState title="No versions available." />
        )}
      </TimelinePanel.Content>
    </TimelinePanel.Drawer>
  );
};
